import React, { useMemo, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

const CONSONANTS = 'BCDFGHJKLMNPQRSTVWXYZ';

const CurpBuilder = ({ formData, onCurpChange }) => {
    const { name, surnames, date_birth, gender_identity, state, foreign } = formData;
    // Stable random consonant per session for single-surname foreigners
    const randomConsonantRef = useRef(CONSONANTS[Math.floor(Math.random() * CONSONANTS.length)]);

    const curp = useMemo(() => {
        const cleanStr = (str) => (str || '').normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim();
        
        const n = cleanStr(name);
        const s = cleanStr(surnames);
        const nameArr = n.split(/\s+/);
        const surnamesArr = s.split(/\s+/);
        
        const firstName = nameArr.length > 1 && (nameArr[0] === 'MARIA' || nameArr[0] === 'JOSE') ? (nameArr[1] || '') : (nameArr[0] || '');
        const firstSurname = surnamesArr[0] || '';
        const hasTwoSurnames = surnamesArr.length > 1;
        const secondSurname = hasTwoSurnames ? surnamesArr[1] : (surnamesArr[0] ? 'X' : '');

        const getFirstVowel = (str) => {
            if (!str || str.length < 2) return '.';
            const match = str.slice(1).match(/[AEIOU]/);
            return match ? match[0] : '.';
        };

        const getFirstInternalConsonant = (str) => {
            if (!str || str.length < 2) return '.';
            const match = str.slice(1).match(/[BCDFGHJKLMNPQRSTVWXYZ]/);
            return match ? match[0] : '.';
        };

        let p1 = firstSurname[0] || '.';
        let p2 = getFirstVowel(firstSurname);
        let p3 = secondSurname[0] || '.';
        let p4 = firstName[0] || '.';

        let p5678910 = '......';
        if (date_birth) {
            const parts = date_birth.split('-');
            if (parts.length === 3) {
                const [year, month, day] = parts;
                p5678910 = (year.slice(-2) || '..').padEnd(2, '.') + (month || '..').padEnd(2, '.') + (day || '..').padEnd(2, '.');
            }
        }

        let p11 = '.';
        if (gender_identity !== '') {
            p11 = (gender_identity === '0' || gender_identity === '3') ? 'M' : 'H';
        }

        let p1213 = foreign ? 'NE' : (state ? state.slice(0, 2).padEnd(2, '.') : '..');

        let p14 = getFirstInternalConsonant(firstSurname);
        // If only one surname, use a stable random consonant for position 15
        let p15 = hasTwoSurnames
            ? getFirstInternalConsonant(secondSurname)
            : randomConsonantRef.current;
        let p16 = getFirstInternalConsonant(firstName);

        let p17 = '.';
        if (date_birth && date_birth.split('-')[0]) {
            p17 = parseInt(date_birth.split('-')[0]) < 2000 ? '0' : 'A';
        }
        let p18 = '1';

        const result = (p1 + p2 + p3 + p4 + p5678910 + p11 + p1213 + p14 + p15 + p16 + p17 + p18).toUpperCase();
        return result.slice(0, 18);
    }, [name, surnames, date_birth, gender_identity, state, foreign]);

    // Notify parent only when the CURP string actually changes (avoids infinite loop)
    const lastEmittedRef = useRef(null);
    useEffect(() => {
        if (onCurpChange && curp !== lastEmittedRef.current) {
            lastEmittedRef.current = curp;
            onCurpChange(curp);
        }
    });

    const content = (
        <AnimatePresence>
            <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                className="fixed bottom-0 left-0 w-full z-[10000] p-4 flex justify-center pointer-events-none"
            >
                <div className="bg-white/95 backdrop-blur-3xl border border-[#f8485e]/40 rounded-full shadow-[0_15px_40px_rgba(0,0,0,0.15)] px-5 py-2.5 flex items-center gap-3 pointer-events-auto ring-4 ring-[#f8485e]/5">
                    <div className="flex flex-col items-start mr-1 text-right">
                        <span className="text-[8px] font-black text-[#f8485e] uppercase tracking-[0.2em] leading-none mb-1">CURP GENERATOR ✨</span>
                        <span className="text-[7px] font-bold text-gray-400 uppercase tracking-tighter leading-none whitespace-nowrap">ID UNICO DE REGISTRO</span>
                    </div>
                    
                    <div className="flex gap-0.5 font-mono text-lg sm:text-xl font-black text-gray-800 tracking-tight">
                        {curp.split('').map((char, i) => (
                            <motion.span
                                key={`${i}-${char}`}
                                initial={{ y: 5, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                className={char === '.' ? "text-gray-200" : "text-[#f8485e]"}
                            >
                                {char}
                            </motion.span>
                        ))}
                    </div>

                    <div className="w-7 h-7 rounded-full bg-[#f8485e]/10 flex items-center justify-center ml-1">
                        <motion.div 
                            animate={{ scale: [1, 1.2, 1] }} 
                            transition={{ duration: 2, repeat: Infinity }}
                            className="w-1.5 h-1.5 rounded-full bg-[#f8485e]" 
                        />
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );

    return ReactDOM.createPortal(content, document.body);
};

export default CurpBuilder;
